import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Tabs,
  Tab,
  Chip,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  PhoneAndroid as PhoneAndroidIcon,
  Computer as ComputerIcon,
  Search as SearchIcon,
  Lock as LockIcon,
  Wifi as WifiIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Apple as AppleIcon,
  Android as AndroidIcon,
} from '@mui/icons-material';

const toolsData = [
  // Network Tools
  {
    name: 'Little Snitch',
    cost: 'Paid',
    platforms: ['Mac'],
    difficulty: 'Medium',
    category: 'Network',
    description: 'The gold standard firewall for Mac. It visualizes every connection your computer makes on a world map.',
    useCase: 'Preventing malware from "phoning home" with your data.',
    link: 'https://www.obdev.at/products/littlesnitch/',
    icon: <SecurityIcon />,
  },
  {
    name: 'LuLu',
    cost: 'Free',
    platforms: ['Mac'],
    difficulty: 'Easy',
    category: 'Network',
    description: 'A free alternative to Little Snitch. It blocks unauthorized outgoing connections.',
    useCase: 'Good for users who want "set and forget" protection without complex maps.',
    link: 'https://objective-see.org/products/lulu.html',
    icon: <SecurityIcon />,
  },
  {
    name: 'Portmaster',
    cost: 'Free',
    platforms: ['Windows', 'Linux'],
    difficulty: 'Easy',
    category: 'Network',
    description: 'Finally, a privacy firewall for Windows. It blocks ads, trackers, and malware at the system level (not just in the browser).',
    useCase: 'Cleaning up a noisy Windows PC and stopping telemetry.',
    link: 'https://safing.io/portmaster/',
    icon: <SecurityIcon />,
  },
  // Mobile Security
  {
    name: 'iVerify',
    cost: 'Low Cost',
    platforms: ['iOS', 'Android'],
    difficulty: 'Easy',
    category: 'Mobile',
    description: 'A "Check Engine Light" for your phone. It scans settings (not files) to ensure you are using maximum security standards.',
    useCase: 'General peace of mind; confirming your phone isn\'t jailbroken/rooted.',
    link: 'https://www.iverify.io/',
    icon: <PhoneAndroidIcon />,
  },
  {
    name: 'Bitdefender Mobile Security',
    cost: 'Freemium',
    platforms: ['iOS', 'Android'],
    difficulty: 'Easy',
    category: 'Mobile',
    description: 'Scans for malicious links in SMS texts (smishing) and calendar invites.',
    useCase: 'Preventing accidental clicks on phishing links.',
    link: 'https://www.bitdefender.com/solutions/mobile-security.html',
    icon: <PhoneAndroidIcon />,
  },
  // Scanners
  {
    name: 'Malwarebytes',
    cost: 'Free',
    platforms: ['Windows', 'Mac', 'Linux', 'iOS', 'Android'],
    difficulty: 'Easy',
    category: 'Scanner',
    description: 'The "Janitor." Great for a one-time scan to remove adware and junk files.',
    useCase: 'Quick cleanup of malware, adware, and potentially unwanted programs.',
    link: 'https://www.malwarebytes.com/',
    icon: <SearchIcon />,
  },
  {
    name: 'ClamTk',
    cost: 'Free',
    platforms: ['Linux'],
    difficulty: 'Easy',
    category: 'Scanner',
    description: 'A graphical front-end for ClamAV antivirus scanner on Linux.',
    useCase: 'Open-source virus scanning for Linux users.',
    link: 'https://github.com/dave-theunsub/clamtk',
    icon: <SearchIcon />,
  },
  // Privacy Tools
  {
    name: 'Bitwarden',
    cost: 'Free',
    platforms: ['Windows', 'Mac', 'Linux', 'iOS', 'Android'],
    difficulty: 'Easy',
    category: 'Privacy',
    description: 'An open-source password vault. Stops you from reusing passwords.',
    useCase: 'Secure password management across all your devices.',
    link: 'https://bitwarden.com/',
    icon: <LockIcon />,
  },
  {
    name: 'VeraCrypt',
    cost: 'Free',
    platforms: ['Windows', 'Mac', 'Linux'],
    difficulty: 'Medium',
    category: 'Privacy',
    description: 'Creates an encrypted "virtual safe" for your most sensitive PDF documents.',
    useCase: 'File and disk encryption for sensitive documents.',
    link: 'https://www.veracrypt.fr/',
    icon: <LockIcon />,
  },
  {
    name: 'TinyCheck',
    cost: 'Free',
    platforms: ['Hardware'],
    difficulty: 'Medium',
    category: 'Forensics',
    description: 'Captures Wi-Fi traffic from a smartphone to detect stalkerware without installing apps on the phone itself.',
    useCase: 'Victims of domestic abuse who fear their abuser checks their phone for antivirus apps.',
    link: 'https://github.com/KasperskyLab/TinyCheck',
    icon: <WifiIcon />,
  },
  // Advanced Forensics
  {
    name: 'MVT (Mobile Verification Toolkit)',
    cost: 'Free',
    platforms: ['Mac', 'Linux'],
    difficulty: 'Expert',
    category: 'Forensics',
    description: 'A command-line tool developed by Amnesty International to identify military-grade spyware (like Pegasus).',
    useCase: 'Advanced forensic analysis of mobile devices for sophisticated threats.',
    link: 'https://github.com/mvt-project/mvt',
    icon: <SearchIcon />,
    hasDeepDive: true,
  },
];

const SecurityHub = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [mvtDialogOpen, setMvtDialogOpen] = useState(false);

  const platformTabs = ['All', 'Windows', 'Mac', 'Linux', 'iOS', 'Android', 'Hardware'];

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const filterToolsByPlatform = (tools) => {
    if (selectedTab === 0) return tools; // All
    const selectedPlatform = platformTabs[selectedTab];
    return tools.filter(tool => tool.platforms.includes(selectedPlatform));
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy':
        return 'success';
      case 'Medium':
        return 'warning';
      case 'Expert':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'iOS':
        return <AppleIcon fontSize="small" />;
      case 'Android':
        return <AndroidIcon fontSize="small" />;
      case 'Mac':
        return <AppleIcon fontSize="small" />;
      case 'Windows':
        return <ComputerIcon fontSize="small" />;
      case 'Linux':
        return <ComputerIcon fontSize="small" />;
      case 'Hardware':
        return <WifiIcon fontSize="small" />;
      default:
        return <ComputerIcon fontSize="small" />;
    }
  };

  const filteredTools = filterToolsByPlatform(toolsData);

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography
          variant="h3"
          gutterBottom
          sx={{
            fontWeight: 700,
            color: '#0d3b66',
            mb: 2,
          }}
        >
          Security Hub
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}
        >
          A comprehensive resource hub for victims of cybercrime. These tools act as your digital bodyguards,
          helping you protect your devices, data, and peace of mind.
        </Typography>
        
        <Alert severity="info" sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}>
          <Typography variant="body2">
            <strong>Important:</strong> Advanced forensic tools should be used with professional guidance.
            Misinterpretation of results can lead to unnecessary panic or missed threats.
          </Typography>
        </Alert>
      </Box>

      {/* Platform Filter Tabs */}
      <Paper elevation={2} sx={{ mb: 4 }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minHeight: 64,
              fontWeight: 600,
            },
            '& .Mui-selected': {
              color: '#d4af37',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#d4af37',
            },
          }}
        >
          {platformTabs.map((tab, index) => (
            <Tab key={index} label={tab} />
          ))}
        </Tabs>
      </Paper>

      {/* Tools Grid */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        {filteredTools.map((tool, index) => (
          <Grid item xs={12} md={6} lg={4} key={index}>
            <Card
              elevation={3}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: '#0d3b66',
                      mr: 2,
                    }}
                  >
                    {tool.icon}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 700, color: '#0d3b66' }}>
                      {tool.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                      <Chip
                        label={tool.cost}
                        size="small"
                        color={tool.cost === 'Free' ? 'success' : 'default'}
                      />
                      <Chip
                        label={tool.difficulty}
                        size="small"
                        color={getDifficultyColor(tool.difficulty)}
                      />
                    </Box>
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {tool.description}
                </Typography>

                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: '#f5f5f5',
                    borderRadius: 1,
                    mb: 2,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#0d3b66' }}>
                    Use Case:
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {tool.useCase}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {tool.platforms.map((platform, idx) => (
                    <Chip
                      key={idx}
                      icon={getPlatformIcon(platform)}
                      label={platform}
                      size="small"
                      variant="outlined"
                      sx={{ borderColor: '#0d3b66', color: '#0d3b66' }}
                    />
                  ))}
                </Box>
              </CardContent>

              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button
                  variant="contained"
                  fullWidth
                  href={tool.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    bgcolor: '#0d3b66',
                    '&:hover': {
                      bgcolor: '#0a2f52',
                    },
                  }}
                >
                  Learn More
                </Button>
                {tool.hasDeepDive && (
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setMvtDialogOpen(true)}
                    sx={{
                      borderColor: '#d4af37',
                      color: '#d4af37',
                      '&:hover': {
                        borderColor: '#c09d2f',
                        bgcolor: 'rgba(212, 175, 55, 0.04)',
                      },
                    }}
                  >
                    How to Interpret Results
                  </Button>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Home Network Hygiene Section */}
      <Paper elevation={3} sx={{ p: 4, bgcolor: '#f8f9fa' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <WifiIcon sx={{ fontSize: 40, color: '#0d3b66', mr: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0d3b66' }}>
            Home Network Hygiene
          </Typography>
        </Box>

        <Typography variant="body1" sx={{ mb: 3 }}>
          Your router is the front door to your digital home. Lock it properly with these essential steps.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <CheckCircleIcon sx={{ color: 'success.main', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Upgrade to WPA3
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" paragraph>
                Go to your router settings and change "Wireless Security" from WPA2 to WPA3.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Why it matters:</strong> WPA3 makes it mathematically nearly impossible for hackers
                to guess your Wi-Fi password, even if they capture your signal. This is the latest standard
                in Wi-Fi security and should be enabled if your router supports it.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <WarningIcon sx={{ color: 'error.main', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Disable WPS
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" paragraph>
                Turn off WPS (Wi-Fi Protected Setup) in your router's admin panel.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Why it matters:</strong> This old feature is a known security hole that allows
                attackers to bypass your password entirely through brute-force attacks. Even with a strong
                password, WPS creates a vulnerability that sophisticated attackers can exploit.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <InfoIcon sx={{ color: 'info.main', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Use a Guest Network
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" paragraph>
                Create a separate Guest Network for your "Smart" devices (TVs, fridges, cameras, IoT devices).
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                <strong>Why it matters:</strong> Keep your laptop and banking devices on the Main Network.
                This network segmentation stops a hacked lightbulb or smart TV from attacking your computer
                or accessing sensitive data on your primary devices.
              </Typography>
              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Pro Tip:</strong> Give your guest network a simple password that's different from
                  your main network. This makes it easy to share with actual guests while keeping your
                  important devices isolated.
                </Typography>
              </Alert>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Paper>

      {/* MVT Deep Dive Dialog */}
      <Dialog
        open={mvtDialogOpen}
        onClose={() => setMvtDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#0d3b66', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WarningIcon sx={{ mr: 2 }} />
            <Typography variant="h6" component="span">
              How to Interpret MVT Results
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Critical Warning:</strong> MVT extracts sensitive data and generates technical reports.
              Do not self-diagnose. A technologist or forensic investigator must review the findings.
              Misinterpreting a normal system file as malware can lead to unnecessary panic.
            </Typography>
          </Alert>

          <Typography variant="h6" sx={{ fontWeight: 600, color: '#0d3b66', mb: 2 }}>
            Understanding the Process
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="The Report"
                secondary="MVT generates a technical analysis report listing IOCs (Indicators of Compromise). 
                These are digital fingerprints left by sophisticated attackers and spyware."
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Visualization Tools"
                secondary="Because the raw text is hard to read, we recommend using MVT-Companion, 
                a tool that creates a web-based interface to visualize the forensic data (call logs, 
                browser history, app activity) in a readable timeline format."
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <WarningIcon color="error" />
              </ListItemIcon>
              <ListItemText
                primary="Expert Analysis Required"
                secondary="The output requires expertise to interpret correctly. System processes, 
                legitimate app behavior, and actual threats can all generate similar patterns. Only 
                trained professionals can distinguish between normal activity and genuine compromise."
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <InfoIcon color="info" />
              </ListItemIcon>
              <ListItemText
                primary="When to Use MVT"
                secondary="This tool is designed for high-risk individuals (journalists, activists, 
                politicians) who may be targeted by nation-state level spyware like Pegasus. If you 
                believe you're a target, contact a cybersecurity firm or digital rights organization."
              />
            </ListItem>
          </List>

          <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Recommended Resources:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• MVT-Companion: https://mvt-companion.com/" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Amnesty International Security Lab" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Electronic Frontier Foundation (EFF)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Access Now Digital Security Helpline" />
              </ListItem>
            </List>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setMvtDialogOpen(false)}
            variant="contained"
            sx={{
              bgcolor: '#0d3b66',
              '&:hover': {
                bgcolor: '#0a2f52',
              },
            }}
          >
            I Understand
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SecurityHub;
